import { Component, Fragment } from "react";
import type { CSSProperties, ErrorInfo, ReactNode } from "react";

import { isChunkLoadError } from "../lib/chunkRecovery.mjs";

/**
 * AJ 2026-10-01: "pages going white when you click through quickly."
 *
 * Before this component nothing on the dashboard caught a render error, so
 * any one (a report chunk that failed to load after a deploy, an exception
 * inside a report) made React unmount the whole app: a white page, no menu,
 * until the reader reloaded. A boundary keeps the failure where it happened
 * and says what to do about it.
 *
 * Three scopes, innermost first:
 *   report  around every report port (reports/index.tsx)
 *   page    around the routed page inside the shell (DashboardShell), so the
 *           Executive, Settings, the staff pages and the widget tabs are
 *           covered and the menu stays up
 *   app     around the whole app (main.tsx), the last resort
 *
 * The card never prints the error itself: the detail goes to the console
 * (the MER-464 rule, loud in the console, quiet on the page).
 *
 * RESET. `resetKey` changing (the reader went to another tab) clears the
 * error, so one broken page never follows the reader around. Retry clears it
 * in place and remounts the children (`onRetry` runs first). For a chunk that
 * failed to load, the retry is a reload of the page: a failed chunk cannot be
 * loaded again in the same document (chunkRecovery.mjs says why), and the
 * reload fetches the current build.
 */

export type ErrorScope = "report" | "page" | "app";

export interface ErrorBoundaryProps {
  children: ReactNode;
  scope: ErrorScope;
  /** Changing it clears a shown error (route path, report id). */
  resetKey?: string;
  /** What failed, for the title: "Disposal", "Executive". */
  label?: string;
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
  attempt: number;
}

export const ERROR_COPY = Object.freeze({
  chunk: {
    title: "did not finish loading",
    body:
      "The dashboard could not load the files for this page. That happens when the " +
      "dashboard was updated while this tab was open, or when the connection dropped. " +
      "Reload to get the current version; the other pages in the menu still work.",
  },
  render: {
    title: "hit a problem",
    body:
      "Something went wrong while showing this page. Nothing was changed or lost. " +
      "Retry, or pick another page from the menu.",
  },
  help: "If it keeps happening, email support@meridiananalytics.io.",
});

const PRIMARY: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#fff",
  cursor: "pointer",
};

const SECONDARY: CSSProperties = {
  ...PRIMARY,
  fontWeight: 500,
  background: "var(--panel)",
  color: "var(--brand-deep)",
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, hasError: false, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error, hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The detail is for engineering; the page shows fixed copy.
    console.error(
      `[error boundary: ${this.props.scope}${this.props.label ? `, ${this.props.label}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, hasError: false });
    }
  }

  retry = (): void => {
    try {
      this.props.onRetry?.();
    } catch {
      // A failing cleanup must not stop the retry.
    }
    this.setState((s) => ({ error: null, hasError: false, attempt: s.attempt + 1 }));
  };

  reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      // Keyed by the attempt: Retry remounts the children from scratch.
      return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
    }
    const kind = isChunkLoadError(this.state.error) ? "chunk" : "render";
    const copy = ERROR_COPY[kind];
    const subject =
      this.props.label || (this.props.scope === "app" ? "The dashboard" : "This page");
    const card = (
      <div
        role="alert"
        data-error-boundary={this.props.scope}
        data-error-kind={kind}
        style={{
          background: "var(--panel)",
          border: "1px solid var(--border)",
          borderLeft: "4px solid var(--flag-yellow)",
          borderRadius: 12,
          boxShadow: "var(--shadow-card)",
          padding: "24px 28px",
          maxWidth: 560,
          display: "grid",
          gap: 10,
          textAlign: "left",
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>
          {subject} {copy.title}
        </div>
        <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--text-2)" }}>{copy.body}</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
          {kind === "chunk" ? (
            <button type="button" style={PRIMARY} onClick={this.reload}>
              Reload and retry
            </button>
          ) : (
            <>
              <button type="button" style={PRIMARY} onClick={this.retry}>
                Retry
              </button>
              <button type="button" style={SECONDARY} onClick={this.reload}>
                Reload the dashboard
              </button>
            </>
          )}
        </div>
        <p style={{ fontSize: 12, color: "var(--text-muted)" }}>{ERROR_COPY.help}</p>
      </div>
    );
    if (this.props.scope === "app") {
      return (
        <main
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            padding: 32,
            background: "var(--bg)",
          }}
        >
          {card}
        </main>
      );
    }
    return <div style={{ padding: "32px", display: "grid", justifyItems: "start" }}>{card}</div>;
  }
}
